import type {
  AircraftSpec,
  CharterRateRow,
  CompanyAccount,
  CostModel,
  FlightLogEntry,
  FlightType,
  LedgerTxn,
  LedgerTxnKind,
  CharterRequest,
  Mission,
  OpsDeskSettings,
  RateTables,
  ReserveRates,
} from "./types";
import { resolveAircraftOperatingCost } from "./aircraftOperatingCost";
import {
  DEFAULT_CURRENCY,
  fuelKindForType,
  hydrateCurrency,
  lookupFuelPrice,
  lookupOpsFee,
  weightClassForType,
  type CurrencyCode,
  type MoneyCatalogs,
} from "./catalogs";
import { fxFieldsFromConversion, type FxConversion } from "./currency";
import { DATA_SOURCE_LABEL, pickPreferredHours, type DataSourceKind, type RankedValue } from "./dataSource";
import { arrivalFuelUsedGal, ofpPlannedFuelGal, resolveTripFuelGal } from "./tripFuel";

/** Fresh company / career seed. Amounts stay on *Cad fields; home currency is CAD default. */
export const DEFAULT_OPENING_BALANCE_CAD = 10_000_000;

export const OPENING_CASH_MEMO = "Opening company cash";
export const OPENING_ADJUST_MEMO = "Opening balance adjustment";

export const DEFAULT_CHARTER_RATES: CharterRateRow[] = [
  { flightType: "golf", perNmCad: 18, perBlockHourCad: 3800, perPaxCad: 150 },
  { flightType: "sun", perNmCad: 16, perBlockHourCad: 3600, perPaxCad: 120 },
  { flightType: "vip", perNmCad: 22, perBlockHourCad: 4500, perPaxCad: 250 },
  { flightType: "corporate", perNmCad: 17, perBlockHourCad: 3700, perPaxCad: 100 },
  { flightType: "medical", perNmCad: 20, perBlockHourCad: 4200, perPaxCad: 0 },
  { flightType: "ferry", perNmCad: 0, perBlockHourCad: 0, perPaxCad: 0 },
];

export const DEFAULT_COST_MODEL: CostModel = {
  fuelCadPerGal: 8.5,
  handlingCad: 450,
  landingCad: 180,
};

/**
 * Per-hour accruals in home currency. Operator-sim — not a shop invoice.
 *
 * Engine CAD 700/hr = both TFE731-5BR on the Hawker 800XP (≈ 350 / engine).
 * Mid of public MSP / JSSI-style midsize turbofan reserve bands on broker DOC
 * sheets, expressed as a CAD planning figure. Not a Honeywell contract.
 *
 * Mx / airframe CAD 380/hr = midsize jet airframe + APU / unscheduled band
 * (Conklin-style light/midsize DOC ranges + Hawker profile). Editable.
 */
export const DEFAULT_RESERVE_RATES: ReserveRates = {
  mxPerHourCad: 380,
  enginePerHourCad: 700,
};

export const DEFAULT_RATES: RateTables = {
  charter: DEFAULT_CHARTER_RATES,
  costs: DEFAULT_COST_MODEL,
  mx: {
    laborCad: 8500,
    partsCad: 4200,
    inspectionCad: 2800,
  },
  reserves: DEFAULT_RESERVE_RATES,
  inspectionIntervalHours: 200,
};

/** New install ledger: no opening line until the user enters capital. */
export function emptyAccount(now = new Date(), homeCurrency: CurrencyCode = DEFAULT_CURRENCY): CompanyAccount {
  void now;
  return {
    balanceCad: 0,
    homeCurrency: hydrateCurrency(homeCurrency),
    transactions: [],
  };
}

export function defaultAccount(
  now = new Date(),
  openingCad = DEFAULT_OPENING_BALANCE_CAD,
  homeCurrency: CurrencyCode = DEFAULT_CURRENCY,
): CompanyAccount {
  const amount = Number.isFinite(openingCad) && openingCad > 0 ? Math.round(openingCad) : DEFAULT_OPENING_BALANCE_CAD;
  const home = hydrateCurrency(homeCurrency);
  return {
    balanceCad: amount,
    homeCurrency: home,
    transactions: [
      {
        id: `txn-open-${now.getTime().toString(36)}`,
        createdAtUtc: now.toISOString(),
        kind: "adjustment",
        amountCad: amount,
        memo: OPENING_CASH_MEMO,
        currency: home,
        idempotencyKey: "opening:cash",
      },
    ],
  };
}

/** Deterministic ledger key — Accept + Complete share this, so they cannot double-post. */
export function ledgerIdempotencyKey(missionId: string, kind: LedgerTxnKind): string {
  return `${missionId}:${kind}`;
}

export function formatCad(amount: number): string {
  const abs = Math.abs(amount).toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return amount < 0 ? `−$${abs}` : `$${abs}`;
}

export function recentTransactions(account: CompanyAccount, n = 8): LedgerTxn[] {
  return account.transactions.slice(0, Math.max(0, n));
}

export function rateForType(rates: RateTables, flightType: FlightType): CharterRateRow {
  return (
    rates.charter.find((r) => r.flightType === flightType) ??
    DEFAULT_CHARTER_RATES.find((r) => r.flightType === flightType) ??
    DEFAULT_CHARTER_RATES[3]!
  );
}

export function hydrateRates(raw?: Partial<RateTables>): RateTables {
  const charterByType = new Map(
    (raw?.charter ?? []).map((row) => [row.flightType, row] as const),
  );
  return {
    charter: DEFAULT_CHARTER_RATES.map((row) => ({
      ...row,
      ...charterByType.get(row.flightType),
    })),
    costs: { ...DEFAULT_COST_MODEL, ...raw?.costs },
    mx: { ...DEFAULT_RATES.mx, ...raw?.mx },
    reserves: { ...DEFAULT_RESERVE_RATES, ...raw?.reserves },
    inspectionIntervalHours: raw?.inspectionIntervalHours ?? DEFAULT_RATES.inspectionIntervalHours,
  };
}

function hydrateLedgerTxn(raw: LedgerTxn, home: CurrencyCode): LedgerTxn {
  return {
    ...raw,
    currency: raw.currency && ["CAD", "USD", "EUR", "GBP"].includes(raw.currency) ? raw.currency : home,
    idempotencyKey: typeof raw.idempotencyKey === "string" && raw.idempotencyKey.trim() ? raw.idempotencyKey : undefined,
    originalAmount: typeof raw.originalAmount === "number" && Number.isFinite(raw.originalAmount) ? raw.originalAmount : undefined,
    originalCurrency:
      raw.originalCurrency && ["CAD", "USD", "EUR", "GBP"].includes(raw.originalCurrency) ? raw.originalCurrency : undefined,
    fxRate: typeof raw.fxRate === "number" && raw.fxRate > 0 ? raw.fxRate : undefined,
    fxNote: typeof raw.fxNote === "string" && raw.fxNote.trim() ? raw.fxNote : undefined,
  };
}

export function hydrateAccount(raw?: Partial<CompanyAccount>, fallbackCurrency?: CurrencyCode): CompanyAccount {
  const home = hydrateCurrency(raw?.homeCurrency ?? fallbackCurrency);
  if (!raw || typeof raw.balanceCad !== "number" || !Array.isArray(raw.transactions)) {
    return defaultAccount(undefined, undefined, home);
  }
  return {
    balanceCad: raw.balanceCad,
    homeCurrency: home,
    transactions: raw.transactions.map((t) => hydrateLedgerTxn(t, home)),
  };
}

export function isOpeningLedgerLine(txn: Pick<LedgerTxn, "kind" | "memo">): boolean {
  if (txn.kind !== "adjustment") return false;
  return /opening company cash|opening balance adjustment/i.test(txn.memo ?? "");
}

/** Effective opening cash: original seed plus later opening-adjustment lines. */
export function openingCapitalFromAccount(
  account: CompanyAccount | undefined,
  fallback = DEFAULT_OPENING_BALANCE_CAD,
): number {
  const lines = account?.transactions.filter(isOpeningLedgerLine) ?? [];
  if (!lines.length) return fallback;
  const sum = Math.round(lines.reduce((s, t) => s + t.amountCad, 0));
  return sum > 0 ? sum : fallback;
}

export type OpeningBalanceApply = {
  account: CompanyAccount;
  posted: boolean;
  deltaCad: number;
  openingCad: number;
};

/**
 * Set opening capital by posting an adjustment. Later charter / fuel / Mx rows stay.
 * Never replaces or deletes the ledger.
 */
export function applyOpeningBalance(
  account: CompanyAccount,
  targetCad: number,
  now = new Date(),
): OpeningBalanceApply {
  const openingCad = Number.isFinite(targetCad) && targetCad > 0 ? Math.round(targetCad) : 0;
  const current = openingCapitalFromAccount(account);
  if (openingCad <= 0) {
    return { account, posted: false, deltaCad: 0, openingCad: current };
  }
  const deltaCad = Math.round((openingCad - current) * 100) / 100;
  if (deltaCad === 0) {
    return { account, posted: false, deltaCad: 0, openingCad: current };
  }
  return {
    account: appendTransactions(
      account,
      [
        {
          createdAtUtc: now.toISOString(),
          kind: "adjustment",
          amountCad: deltaCad,
          memo: OPENING_ADJUST_MEMO,
        },
      ],
      now,
    ),
    posted: true,
    deltaCad,
    openingCad,
  };
}

function newTxnId(kind: LedgerTxnKind, now: Date): string {
  return `txn-${kind}-${now.getTime().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

/**
 * Replace posted fuel lines for a trip (estimated settle burn → actual arrival burn).
 * When `missionId` is missing, appends a new fuel debit only.
 */
function replaceTripKindTxn(
  account: CompanyAccount,
  row: Omit<LedgerTxn, "id">,
  kind: LedgerTxnKind,
  now = new Date(),
): CompanyAccount {
  const missionId = row.missionId?.trim();
  const key = row.idempotencyKey ?? (missionId ? ledgerIdempotencyKey(missionId, kind) : undefined);
  const keyed: Omit<LedgerTxn, "id"> = {
    ...row,
    kind,
    currency: row.currency ?? account.homeCurrency ?? DEFAULT_CURRENCY,
    idempotencyKey: key,
  };
  if (!missionId && !key) {
    return appendTransactions(account, [keyed], now);
  }
  const existing = account.transactions.filter((t) =>
    Boolean((key && t.idempotencyKey === key) || (missionId && t.kind === kind && t.missionId === missionId)),
  );
  if (!existing.length) {
    return appendTransactions(account, [keyed], now);
  }
  const current = existing.reduce((sum, t) => sum + t.amountCad, 0);
  if (Math.round(current * 100) === Math.round(keyed.amountCad * 100) && existing.length === 1) {
    return {
      ...account,
      transactions: account.transactions.map((t) =>
        existing.some((e) => e.id === t.id) ? { ...t, memo: keyed.memo, createdAtUtc: keyed.createdAtUtc ?? t.createdAtUtc } : t,
      ),
    };
  }
  const restored = existing.reduce((sum, t) => sum + t.amountCad, 0);
  const skip = new Set(existing.map((t) => t.id));
  const without: CompanyAccount = {
    ...account,
    balanceCad: Math.round((account.balanceCad - restored) * 100) / 100,
    transactions: account.transactions.filter((t) => !skip.has(t.id)),
  };
  return appendTransactions(without, [keyed], now);
}

/**
 * Replace posted parking for a trip (1-day estimate → actual dwell nights).
 */
export function replaceTripParkingTxn(
  account: CompanyAccount,
  row: Omit<LedgerTxn, "id">,
  now = new Date(),
): CompanyAccount {
  return replaceTripKindTxn(account, row, "parking", now);
}

/** Replace posted crew-hotel for a trip (nights update when dwell is known). */
export function replaceTripHotelTxn(
  account: CompanyAccount,
  row: Omit<LedgerTxn, "id">,
  now = new Date(),
): CompanyAccount {
  return replaceTripKindTxn(account, row, "hotel", now);
}

/** Remove every company-ledger row for a flight and hand the cash back. */
export function voidMissionLedger(account: CompanyAccount, missionId: string): CompanyAccount {
  const id = missionId.trim();
  if (!id) return account;
  const removed = account.transactions.filter((txn) => txn.missionId === id);
  if (!removed.length) return account;
  const restored = removed.reduce((sum, txn) => sum + txn.amountCad, 0);
  return {
    ...account,
    balanceCad: Math.round((account.balanceCad - restored) * 100) / 100,
    transactions: account.transactions.filter((txn) => txn.missionId !== id),
  };
}

/**
 * Remove posted lines of one kind for a trip and hand the cash back.
 * Used when grounding says the desk posted against a flight that never flew —
 * the ghost row leaves, the balance is restored, no reversal clutter.
 */
export function voidTripKindTxn(
  account: CompanyAccount,
  kind: LedgerTxnKind,
  match: (txn: LedgerTxn) => boolean,
): { account: CompanyAccount; removed: LedgerTxn[] } {
  const removed = account.transactions.filter((t) => t.kind === kind && match(t));
  if (!removed.length) return { account, removed: [] };
  const restored = removed.reduce((sum, t) => sum + t.amountCad, 0);
  const skip = new Set(removed.map((t) => t.id));
  return {
    account: {
      ...account,
      balanceCad: Math.round((account.balanceCad - restored) * 100) / 100,
      transactions: account.transactions.filter((t) => !skip.has(t.id)),
    },
    removed,
  };
}

export function replaceTripFuelTxn(
  account: CompanyAccount,
  row: Omit<LedgerTxn, "id">,
  now = new Date(),
): CompanyAccount {
  const missionId = row.missionId?.trim();
  const key = row.idempotencyKey ?? (missionId ? ledgerIdempotencyKey(missionId, "fuel") : undefined);
  const keyed: Omit<LedgerTxn, "id"> = {
    ...row,
    currency: row.currency ?? account.homeCurrency ?? DEFAULT_CURRENCY,
    idempotencyKey: key,
  };
  if (!missionId && !key) {
    return appendTransactions(account, [keyed], now);
  }
  const existing = account.transactions.filter((t) =>
    Boolean((key && t.idempotencyKey === key) || (missionId && t.kind === "fuel" && t.missionId === missionId)),
  );
  if (!existing.length) {
    return appendTransactions(account, [keyed], now);
  }
  const restored = existing.reduce((sum, t) => sum + t.amountCad, 0);
  const skip = new Set(existing.map((t) => t.id));
  const without: CompanyAccount = {
    ...account,
    balanceCad: Math.round((account.balanceCad - restored) * 100) / 100,
    transactions: account.transactions.filter((t) => !skip.has(t.id)),
  };
  return appendTransactions(without, [keyed], now);
}

export function appendTransactions(
  account: CompanyAccount,
  rows: Omit<LedgerTxn, "id">[],
  now = new Date(),
): CompanyAccount {
  if (!rows.length) return account;
  const existingKeys = new Set(
    account.transactions.map((t) => t.idempotencyKey).filter((k): k is string => Boolean(k)),
  );
  const fresh = rows.filter((row) => !row.idempotencyKey || !existingKeys.has(row.idempotencyKey));
  if (!fresh.length) return account;
  const home = hydrateCurrency(account.homeCurrency);
  const txns: LedgerTxn[] = fresh.map((row) => ({
    ...row,
    id: newTxnId(row.kind, now),
    createdAtUtc: row.createdAtUtc || now.toISOString(),
    currency: row.currency ?? home,
  }));
  const delta = txns.reduce((sum, t) => sum + t.amountCad, 0);
  return {
    ...account,
    homeCurrency: account.homeCurrency ?? home,
    balanceCad: Math.round((account.balanceCad + delta) * 100) / 100,
    transactions: [...txns, ...account.transactions],
  };
}

export type HoursQuoteOpts = {
  /** AAR / post-flight engine hours the user filed. Wins over sim, OFP, and ETE. */
  userHours?: number;
  /** SimConnect engine time: combustion, else N1, else the phase fallback already on the trip. */
  simHours?: number;
  /** SimBrief OFP planned time (hours). Preferred over generator ETE. */
  ofpPlannedHours?: number;
};

/** SimBrief enroute seconds on the ticket, in hours. Not a second clock. */
export function ofpPlannedBlockHours(mission: Pick<Mission, "ofpAttachment">): number | undefined {
  const sec = Number(mission.ofpAttachment?.eteSec);
  if (!Number.isFinite(sec) || sec <= 0) return undefined;
  return sec / 3600;
}

function positiveHours(value: number | null | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null;
  return value;
}

/**
 * One hour clock for Mx, engine, and insurance hourly.
 * User AAR edit → SimConnect combustion/N1 → SimBrief OFP → desk ETE.
 * Out/in timestamps are not a separate DOC hour.
 */
export function resolveBillableHours(
  mission: Mission,
  log?: FlightLogEntry,
  opts?: HoursQuoteOpts,
): RankedValue<number> | null {
  const ete = mission.eteMin > 0 ? mission.eteMin / 60 : null;
  return pickPreferredHours({
    userOverride: positiveHours(opts?.userHours ?? log?.arrival?.engineHours),
    simActual: positiveHours(opts?.simHours),
    ofpPlanned: positiveHours(opts?.ofpPlannedHours ?? ofpPlannedBlockHours(mission)),
    deskEstimate: ete,
  });
}

/** DOC / reserve hours: AAR edit, else sim engine time, else OFP, else planned ETE. */
export function billableHours(mission: Mission, log?: FlightLogEntry, opts?: HoursQuoteOpts): number {
  return resolveBillableHours(mission, log, opts)?.value ?? 0;
}

/** Fields used to decide whether a trip / RFQ is a paying charter. */
export type RevenueClassifiable = {
  flightType?: FlightType;
  isMaintenance?: boolean;
  mxPositioning?: boolean;
  paxCount?: number;
  cargoLb?: number;
  clientName?: string;
};

/** Paying souls or declared cargo — the taken cheaper mx-positioning offer. */
export function hasPayingCharterLoad(row: Pick<RevenueClassifiable, "paxCount" | "cargoLb">): boolean {
  return (row.paxCount ?? 0) > 0 || (row.cargoLb ?? 0) > 0;
}

/** Mx-positioning exception: someone took the cheaper charter on that shop leg. */
export function isPayingMxPositioning(row: RevenueClassifiable): boolean {
  return Boolean(row.mxPositioning) && hasPayingCharterLoad(row);
}

/**
 * Empty company / mx ferry — positioning with no taken paying charter.
 * Never a revenue flight.
 */
export function isEmptyFerry(row: RevenueClassifiable): boolean {
  if (isPayingMxPositioning(row)) return false;
  if (row.flightType === "ferry") return true;
  if (row.isMaintenance) return true;
  if (/^company ferry$/i.test(row.clientName ?? "")) return true;
  return false;
}

/**
 * Charter income only for a real paying charter. Empty ferries never qualify.
 * Exception: accepted cheaper-than-usual charter on an mx positioning leg.
 */
export function isRevenueCharter(row: RevenueClassifiable | Mission): boolean {
  if (isPayingMxPositioning(row)) return true;
  if (isEmptyFerry(row)) return false;
  if (row.flightType === "ferry" || row.isMaintenance) return false;
  return true;
}

export type CharterQuote = {
  revenueCad: number;
  fuelCad: number;
  handlingCad: number;
  landingCad: number;
  parkingCad: number;
  mxReserveCad: number;
  engineReserveCad: number;
  hours: number;
  hoursSource: DataSourceKind;
  fuelGal: number;
  fuelSource: DataSourceKind;
  fuelIcao: string;
  opsIcao: string;
};

export type QuoteOpts = HoursQuoteOpts & {
  catalogs?: MoneyCatalogs;
  aircraft?: Partial<Pick<AircraftSpec, "id" | "registration" | "cruiseKt" | "rangeNm">> &
    Pick<AircraftSpec, "icaoType" | "name">;
  /**
   * Variable hourly DOC. A set Mx or engine component overrides the company
   * reserve for this tail. Fuel planning on that table does not replace
   * measured fuel. Omit to keep company reserves.
   */
  opsDesk?: OpsDeskSettings | null;
  homeCurrency?: CurrencyCode;
  /** Arrival / measured fuel in US gal — marks fuel as actual when quoting. */
  actualFuelGal?: number;
  /** SimConnect trip burn in US gal. Preferred over OFP / desk estimate when arrival is missing. */
  simFuelGal?: number;
  /** SimBrief OFP planned burn in US gal. Preferred over generator `mission.fuelGal`. */
  ofpPlannedFuelGal?: number;
  /**
   * Parking nights. Omit = 1-day proposal estimate. 0 = quick turn (no parking).
   * Actual dwell sets this from logged touchdown → next departure.
   */
  parkingDays?: number;
  /**
   * Accepted inbox desk quote. When set on a paying charter, this is the
   * revenue figure — not a second rate-card total. Hours still drive reserves.
   */
  acceptedRevenueCad?: number;
};

export function resolveQuoteFuel(
  mission: Mission,
  log?: FlightLogEntry,
  opts?: QuoteOpts,
): RankedValue<number> | null {
  return resolveTripFuelGal({
    userArrivalGal: opts?.actualFuelGal ?? arrivalFuelUsedGal(log),
    simUsedGal: opts?.simFuelGal,
    ofpPlannedGal: opts?.ofpPlannedFuelGal ?? ofpPlannedFuelGal(mission.ofpAttachment),
    deskEstimateGal: mission.fuelGal,
  });
}

export function resolveQuoteFuelGal(
  mission: Mission,
  log?: FlightLogEntry,
  opts?: QuoteOpts,
): number {
  return resolveQuoteFuel(mission, log, opts)?.value ?? mission.fuelGal;
}

/**
 * Mx / engine from variable DOC when that component is set. Fuel planning
 * on the same row is not applied here — fuelCad stays measured burn × price.
 */
function docReserveRates(opts?: QuoteOpts): { mxPerHour: number; enginePerHour: number } {
  const aircraft = opts?.aircraft;
  if (!opts?.opsDesk || !aircraft?.icaoType) return { mxPerHour: 0, enginePerHour: 0 };
  const resolved = resolveAircraftOperatingCost(
    {
      id: aircraft.id ?? "",
      registration: aircraft.registration ?? "",
      icaoType: aircraft.icaoType,
      cruiseKt: aircraft.cruiseKt ?? 0,
      rangeNm: aircraft.rangeNm ?? 0,
    },
    opts.opsDesk,
  );
  return { mxPerHour: resolved.mxPerHour, enginePerHour: resolved.enginePerHour };
}

/**
 * The inbox desk quote for this mission. A ferry that shares the RFQ id does
 * not inherit the charter fee. Null when no accepted figure is on file.
 */
export function acceptedQuoteRevenueCad(
  mission: Pick<Mission, "id" | "inboxRequestId">,
  inbox: readonly Pick<CharterRequest, "id" | "acceptedMissionId" | "deskQuote">[] | null | undefined,
): number | null {
  if (!inbox?.length || !mission.id) return null;
  const row =
    inbox.find((item) => item.acceptedMissionId === mission.id) ??
    inbox.find(
      (item) =>
        Boolean(mission.inboxRequestId) &&
        item.id === mission.inboxRequestId &&
        (!item.acceptedMissionId || item.acceptedMissionId === mission.id),
    );
  const value = row?.deskQuote?.revenueCad;
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100) / 100;
}

export function quoteCharter(
  mission: Mission,
  rates: RateTables,
  log?: FlightLogEntry,
  opts?: QuoteOpts,
): CharterQuote {
  const hoursPick = resolveBillableHours(mission, log, opts);
  const hours = hoursPick?.value ?? 0;
  const hoursSource: DataSourceKind = hoursPick?.source ?? "desk_estimate";
  const fuelPick = resolveQuoteFuel(mission, log, opts);
  const burnGal = fuelPick?.value ?? mission.fuelGal;
  const fuelSource: DataSourceKind = fuelPick?.source ?? "desk_estimate";
  const row = rateForType(rates, mission.flightType);
  const factor =
    typeof mission.revenueFactor === "number" && Number.isFinite(mission.revenueFactor)
      ? Math.min(1, Math.max(0, mission.revenueFactor))
      : 1;
  const accepted =
    typeof opts?.acceptedRevenueCad === "number" &&
    Number.isFinite(opts.acceptedRevenueCad) &&
    opts.acceptedRevenueCad > 0
      ? Math.round(opts.acceptedRevenueCad * 100) / 100
      : 0;
  const revenueCad = isRevenueCharter(mission)
    ? accepted > 0
      ? accepted
      : (mission.distanceNm * row.perNmCad + hours * row.perBlockHourCad + mission.paxCount * row.perPaxCad) * factor
    : 0;
  const fuelIcao = mission.originIcao || mission.destIcao;
  const opsIcao = mission.destIcao || mission.originIcao;
  const fuelKind = fuelKindForType(opts?.aircraft?.icaoType);
  const perGal = lookupFuelPrice(opts?.catalogs, fuelIcao, fuelKind, rates.costs.fuelCadPerGal);
  const fuelCad = burnGal * perGal;
  const cls = weightClassForType(opts?.aircraft?.icaoType);
  const reserves = rates.reserves ?? DEFAULT_RESERVE_RATES;
  const doc = docReserveRates(opts);
  const mxPerHour = doc.mxPerHour > 0 ? doc.mxPerHour : (reserves.mxPerHourCad ?? 0);
  const enginePerHour = doc.enginePerHour > 0 ? doc.enginePerHour : (reserves.enginePerHourCad ?? 0);
  const mxReserveCad = hours * mxPerHour;
  const engineReserveCad = hours * enginePerHour;
  const parkingDayRate = lookupOpsFee(opts?.catalogs, opsIcao, "parking", 0, cls);
  const parkingDays = typeof opts?.parkingDays === "number" && Number.isFinite(opts.parkingDays) ? Math.max(0, opts.parkingDays) : 1;
  const parkingCad = parkingDayRate > 0 && parkingDays > 0 ? Math.round(parkingDayRate * parkingDays * 100) / 100 : 0;
  return {
    revenueCad: Math.round(revenueCad * 100) / 100,
    fuelCad: Math.round(fuelCad * 100) / 100,
    handlingCad: lookupOpsFee(opts?.catalogs, opsIcao, "handling", rates.costs.handlingCad, cls),
    landingCad: lookupOpsFee(opts?.catalogs, opsIcao, "landing", rates.costs.landingCad, cls),
    parkingCad,
    mxReserveCad: Math.round(mxReserveCad * 100) / 100,
    engineReserveCad: Math.round(engineReserveCad * 100) / 100,
    hours,
    hoursSource,
    fuelGal: burnGal,
    fuelSource,
    fuelIcao,
    opsIcao,
  };
}

function line(
  iso: string,
  mission: Mission,
  kind: LedgerTxnKind,
  amountCad: number,
  memo: string,
  home: CurrencyCode,
  fx?: FxConversion,
): Omit<LedgerTxn, "id"> {
  return {
    createdAtUtc: iso,
    kind,
    amountCad,
    memo,
    missionId: mission.id,
    aircraftId: mission.aircraftId,
    currency: home,
    idempotencyKey: ledgerIdempotencyKey(mission.id, kind),
    ...fxFieldsFromConversion(fx),
  };
}

export function charterLedgerRows(
  mission: Mission,
  rates: RateTables,
  log: FlightLogEntry | undefined,
  now = new Date(),
  opts?: QuoteOpts,
): Omit<LedgerTxn, "id">[] {
  const quote = quoteCharter(mission, rates, log, opts);
  const iso = now.toISOString();
  const route = `${mission.originIcao}–${mission.destIcao}`;
  const home = hydrateCurrency(opts?.homeCurrency ?? opts?.catalogs?.currency);
  const rows: Omit<LedgerTxn, "id">[] = [];
  if (quote.revenueCad > 0) {
    rows.push(line(iso, mission, "charter_revenue", quote.revenueCad, `Charter ${mission.callsign} ${route} ${mission.flightType}`, home));
  }
  if (quote.fuelCad > 0) {
    const gal = Math.round(quote.fuelGal * 10) / 10;
    const source = DATA_SOURCE_LABEL[quote.fuelSource];
    rows.push(
      line(
        iso,
        mission,
        "fuel",
        -quote.fuelCad,
        `Fuel ${gal} gal (${source}) ${mission.registration} ${quote.fuelIcao}`,
        home,
      ),
    );
  }
  if (quote.handlingCad > 0) {
    rows.push(line(iso, mission, "handling", -quote.handlingCad, `Handling ${mission.destIcao}`, home));
  }
  if (quote.landingCad > 0) {
    rows.push(line(iso, mission, "landing", -quote.landingCad, `Landing ${mission.destIcao}`, home));
  }
  if (quote.parkingCad > 0) {
    rows.push(line(iso, mission, "parking", -quote.parkingCad, `Parking ${mission.destIcao}`, home));
  }
  if (quote.mxReserveCad > 0) {
    rows.push(
      line(
        iso,
        mission,
        "mx_reserve",
        -quote.mxReserveCad,
        `Mx reserve ${quote.hours.toFixed(2)} h (${DATA_SOURCE_LABEL[quote.hoursSource]}) ${mission.registration}`,
        home,
      ),
    );
  }
  if (quote.engineReserveCad > 0) {
    rows.push(
      line(
        iso,
        mission,
        "engine_reserve",
        -quote.engineReserveCad,
        `Engine reserve ${quote.hours.toFixed(2)} h (${DATA_SOURCE_LABEL[quote.hoursSource]}) ${mission.registration}`,
        home,
      ),
    );
  }
  return rows;
}

export function mxLedgerRows(
  aircraftId: string,
  registration: string,
  costs: { laborCad: number; partsCad: number; inspectionCad: number },
  now = new Date(),
): Omit<LedgerTxn, "id">[] {
  const iso = now.toISOString();
  const rows: Omit<LedgerTxn, "id">[] = [];
  if (costs.laborCad > 0) {
    rows.push({
      createdAtUtc: iso,
      kind: "mx_labor",
      amountCad: -costs.laborCad,
      memo: `Mx labor ${registration}`,
      aircraftId,
    });
  }
  if (costs.partsCad > 0) {
    rows.push({
      createdAtUtc: iso,
      kind: "mx_parts",
      amountCad: -costs.partsCad,
      memo: `Mx parts ${registration}`,
      aircraftId,
    });
  }
  if (costs.inspectionCad > 0) {
    rows.push({
      createdAtUtc: iso,
      kind: "mx_inspection",
      amountCad: -costs.inspectionCad,
      memo: `Inspection ${registration}`,
      aircraftId,
    });
  }
  return rows;
}

export function postCharterIfNeeded(
  account: CompanyAccount,
  mission: Mission,
  rates: RateTables,
  log: FlightLogEntry | undefined,
  now = new Date(),
  opts?: QuoteOpts,
): { account: CompanyAccount; posted: boolean } {
  if (mission.ledgerPosted) return { account, posted: false };
  const rows = charterLedgerRows(mission, rates, log, now, opts);
  if (!rows.length) return { account, posted: true };
  return { account: appendTransactions(account, rows, now), posted: true };
}

const RESERVE_KINDS = ["mx_reserve", "engine_reserve"] as const;

function replaceMissionKindTxn(account: CompanyAccount, row: Omit<LedgerTxn, "id">, now = new Date()): CompanyAccount {
  const missionId = row.missionId?.trim();
  const key = row.idempotencyKey ?? (missionId ? ledgerIdempotencyKey(missionId, row.kind) : undefined);
  const keyed: Omit<LedgerTxn, "id"> = {
    ...row,
    currency: row.currency ?? account.homeCurrency ?? DEFAULT_CURRENCY,
    idempotencyKey: key,
  };
  const existing = account.transactions.filter(
    (txn) => (key && txn.idempotencyKey === key) || (missionId && txn.kind === row.kind && txn.missionId === missionId),
  );
  if (!existing.length) return appendTransactions(account, [keyed], now);
  const restored = existing.reduce((sum, txn) => sum + txn.amountCad, 0);
  const skip = new Set(existing.map((txn) => txn.id));
  const without: CompanyAccount = {
    ...account,
    balanceCad: Math.round((account.balanceCad - restored) * 100) / 100,
    transactions: account.transactions.filter((txn) => !skip.has(txn.id)),
  };
  return appendTransactions(without, [keyed], now);
}

/**
 * Replace an already-posted Mx / engine reserve when the closed-trip hours
 * arrive. Does not add a reserve that was never posted, and does not touch
 * charter revenue. Same account reference when the amount and memo are unchanged.
 */
export function refreshCharterReserves(
  account: CompanyAccount,
  mission: Mission,
  rates: RateTables,
  log: FlightLogEntry | undefined,
  now = new Date(),
  opts?: QuoteOpts,
): CompanyAccount {
  if (!mission.ledgerPosted) return account;
  const rows = charterLedgerRows(mission, rates, log, now, opts);
  let next = account;
  for (const kind of RESERVE_KINDS) {
    const row = rows.find((item) => item.kind === kind);
    if (!row) continue;
    const key = ledgerIdempotencyKey(mission.id, kind);
    const existing = next.transactions.filter(
      (txn) => txn.idempotencyKey === key || (txn.kind === kind && txn.missionId === mission.id),
    );
    if (!existing.length) continue;
    if (existing.length === 1 && existing[0]!.amountCad === row.amountCad && existing[0]!.memo === row.memo) continue;
    next = replaceMissionKindTxn(next, row, now);
  }
  return next;
}
